// controllers/trainingController.js
import apiService from '../services/apiService';
import Training from '../models/Training';

export const trainingController = {
  /**
   * Get all trainings with filters and pagination
   * @param {Object} params - { page, limit, status, trainingType, mode, search, sortBy, sortOrder }
   * @returns {Promise<Object>} - { success, data, pagination }
   */
  getAll: async (params = {}) => {
    try {
      const response = await apiService.get('training', params);
      
      // Convert to Training instances
      const trainings = response.data?.map(item => Training.fromJSON(item)) || [];
      
      return { 
        success: true, 
        data: trainings,
        pagination: response.pagination,
        total: response.pagination?.total || trainings.length
      };
    } catch (error) {
      console.error('Error fetching trainings:', error);
      return { 
        success: false, 
        data: [], 
        message: error.message || 'Failed to fetch trainings',
        error: error
      };
    }
  },

  /**
   * Get single training by ID
   * @param {string} id - Training ID
   * @returns {Promise<Object>} - { success, data }
   */
  getById: async (id) => {
    try {
      const response = await apiService.get(`training/${id}`);
      
      return { 
        success: true, 
        data: Training.fromJSON(response.data) 
      };
    } catch (error) {
      console.error('Error fetching training:', error);
      return { 
        success: false, 
        data: null, 
        message: error.message || 'Failed to fetch training',
        error: error
      };
    }
  },

  /**
   * Create new training
   * @param {Object} data - Training data
   * @returns {Promise<Object>} - { success, data, message }
   */
  create: async (data) => {
    try {
      // Validate on client side
      const training = new Training(data);
      const validation = training.validate();
      
      if (!validation.isValid) {
        return { 
          success: false, 
          errors: validation.errors,
          message: 'Validation failed'
        };
      }
      
      const response = await apiService.post('training', data);
      
      return { 
        success: true, 
        data: Training.fromJSON(response.data), 
        message: response.message || 'Training created successfully' 
      };
    } catch (error) {
      console.error('Error creating training:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to create training',
        errors: error.data?.errors || [error.message],
        error: error
      };
    }
  },

  /**
   * Update training
   * @param {string} id - Training ID
   * @param {Object} data - Updated data
   * @returns {Promise<Object>} - { success, data, message }
   */
  update: async (id, data) => {
    try {
      // Validate on client side
      const training = new Training({ ...data, id });
      const validation = training.validate();
      
      if (!validation.isValid) {
        return { 
          success: false, 
          errors: validation.errors,
          message: 'Validation failed'
        };
      }
      
      const response = await apiService.put(`training/${id}`, data);
      
      return { 
        success: true, 
        data: Training.fromJSON(response.data), 
        message: response.message || 'Training updated successfully' 
      };
    } catch (error) {
      console.error('Error updating training:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to update training',
        errors: error.data?.errors || [error.message],
        error: error
      };
    }
  },

  /**
   * Delete training
   * @param {string} id - Training ID
   * @returns {Promise<Object>} - { success, message }
   */
  delete: async (id) => {
    try {
      const response = await apiService.delete(`training/${id}`);
      
      return { 
        success: true, 
        message: response.message || 'Training deleted successfully' 
      };
    } catch (error) {
      console.error('Error deleting training:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to delete training',
        error: error
      };
    }
  },

  /**
   * Enroll employee in training
   * @param {string} trainingId - Training ID
   * @param {string} employeeId - Employee ID
   * @returns {Promise<Object>} - { success, data, message }
   */
  enroll: async (trainingId, employeeId) => {
    try {
      const response = await apiService.post(`training/${trainingId}/enroll`, { 
        employeeId 
      });
      
      return { 
        success: true, 
        data: Training.fromJSON(response.data), 
        message: response.message || 'Enrolled successfully' 
      };
    } catch (error) {
      console.error('Error enrolling:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to enroll',
        error: error
      };
    }
  },

  /**
   * Unenroll employee from training
   * @param {string} trainingId - Training ID
   * @param {string} employeeId - Employee ID
   * @returns {Promise<Object>} - { success, data, message }
   */
  unenroll: async (trainingId, employeeId) => {
    try {
      const response = await apiService.post(`training/${trainingId}/unenroll`, { 
        employeeId 
      });
      
      return { 
        success: true, 
        data: Training.fromJSON(response.data), 
        message: response.message || 'Unenrolled successfully' 
      };
    } catch (error) {
      console.error('Error unenrolling:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to unenroll',
        error: error
      };
    }
  },

  /**
   * Update training status
   * @param {string} id - Training ID
   * @param {string} status - New status
   * @returns {Promise<Object>} - { success, data, message }
   */
  updateStatus: async (id, status) => {
    try {
      const response = await apiService.patch(`training/${id}/status`, { status });
      
      return { 
        success: true, 
        data: Training.fromJSON(response.data), 
        message: response.message || `Status updated to ${status}` 
      };
    } catch (error) {
      console.error('Error updating status:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to update status',
        error: error
      };
    }
  },

  /**
   * Get training statistics
   * @param {Object} filters - { startDate, endDate, trainingType, status }
   * @returns {Promise<Object>} - { success, data }
   */
  getStatistics: async (filters = {}) => {
    try {
      const response = await apiService.get('training/statistics', filters);
      
      return { 
        success: true, 
        data: response.data 
      };
    } catch (error) {
      console.error('Error fetching statistics:', error);
      return { 
        success: false, 
        data: null, 
        message: error.message || 'Failed to fetch statistics',
        error: error
      };
    }
  },

  /**
   * Get upcoming trainings
   * @param {number} days - Number of days to look ahead
   * @returns {Promise<Object>} - { success, data, total }
   */
  getUpcoming: async (days = 30) => {
    try {
      const response = await apiService.get('training/upcoming', { days });
      const trainings = response.data?.map(item => Training.fromJSON(item)) || [];
      
      return { 
        success: true, 
        data: trainings, 
        total: trainings.length 
      };
    } catch (error) {
      console.error('Error fetching upcoming trainings:', error);
      return { 
        success: false, 
        data: [], 
        message: error.message || 'Failed to fetch upcoming trainings',
        error: error
      };
    }
  },

  /**
   * Get employee's trainings
   * @param {string} employeeId - Employee ID
   * @returns {Promise<Object>} - { success, data, total }
   */
  getEmployeeTrainings: async (employeeId) => {
    try {
      const response = await apiService.get(`training/employee/${employeeId}`);
      const trainings = response.data?.map(item => Training.fromJSON(item)) || [];
      
      return { 
        success: true, 
        data: trainings, 
        total: trainings.length 
      };
    } catch (error) {
      console.error('Error fetching employee trainings:', error);
      return { 
        success: false, 
        data: [], 
        message: error.message || 'Failed to fetch employee trainings',
        error: error
      };
    }
  },

  /**
   * Get training participants
   * @param {string} trainingId - Training ID
   * @returns {Promise<Object>} - { success, data, total }
   */
  getParticipants: async (trainingId) => {
    try {
      const response = await apiService.get(`training/${trainingId}/participants`);
      
      return { 
        success: true, 
        data: response.data || [], 
        total: response.total || 0 
      };
    } catch (error) {
      console.error('Error fetching participants:', error);
      return { 
        success: false, 
        data: [], 
        message: error.message || 'Failed to fetch participants',
        error: error
      };
    }
  },

  /**
   * Submit training feedback
   * @param {string} trainingId - Training ID
   * @param {Object} feedbackData - { employeeId, rating, comments }
   * @returns {Promise<Object>} - { success, data, message }
   */
  submitFeedback: async (trainingId, feedbackData) => {
    try {
      const response = await apiService.post(`training/${trainingId}/feedback`, feedbackData);
      
      return { 
        success: true, 
        data: Training.fromJSON(response.data),
        message: response.message || 'Feedback submitted successfully'
      };
    } catch (error) {
      console.error('Error submitting feedback:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to submit feedback',
        error: error
      };
    }
  },

  /**
   * Upload training material
   * @param {string} trainingId - Training ID
   * @param {File} file - File to upload
   * @returns {Promise<Object>} - { success, data, message }
   */
  uploadMaterial: async (trainingId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await apiService.upload(`training/${trainingId}/upload`, formData);
      
      return { 
        success: true, 
        data: response.data,
        message: response.message || 'Material uploaded successfully' 
      };
    } catch (error) {
      console.error('Error uploading material:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to upload material',
        error: error
      };
    }
  },

  /**
   * Export trainings
   * @param {Object} filters - Export filters
   * @param {string} format - 'csv' | 'excel' | 'pdf'
   * @returns {Promise<Object>} - { success, data }
   */
  export: async (filters = {}, format = 'csv') => {
    try {
      const response = await apiService.get('training/export', { 
        ...filters, 
        format 
      });
      
      if (format === 'csv') {
        return { 
          success: true, 
          data: response.data || response
        };
      }
      
      return { 
        success: true, 
        data: response.data 
      };
    } catch (error) {
      console.error('Error exporting trainings:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to export trainings',
        error: error
      };
    }
  },

  /**
   * Regenerate Google Meet link
   * @param {string} trainingId - Training ID
   * @returns {Promise<Object>} - { success, data, message }
   */
  regenerateMeetLink: async (trainingId) => {
    try {
      const response = await apiService.post(`training/${trainingId}/regenerate-meet`);
      
      return { 
        success: true, 
        data: response.data,
        message: response.message || 'Google Meet link regenerated successfully'
      };
    } catch (error) {
      console.error('Error regenerating meet link:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to regenerate meet link',
        error: error
      };
    }
  },

  /**
   * Calculate duration between two dates
   * @param {string} startDate - Start date
   * @param {string} endDate - End date
   * @returns {string} - Duration string
   */
  calculateDuration: (startDate, endDate) => {
    if (!startDate || !endDate) return '';
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return '1 day';
    if (diffDays === 1) return '1 day';
    if (diffDays < 7) return `${diffDays} days`;
    if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return weeks === 1 ? '1 week' : `${weeks} weeks`;
    }
    const months = Math.floor(diffDays / 30);
    return months === 1 ? '1 month' : `${months} months`;
  }
};

export default trainingController;